import type { PaperWidth, ReceiptData, PosSettings } from '@/types';
import { formatMoney } from '@/utils/currency';
import { normalizeLayoutMode, parseReceiptTemplateSettings } from '@/utils/receiptTemplate';
import { getSellerTaxLines, shouldShowZatcaBlock, getDisplayCompanyName, getZatcaReceiptSummary } from '@/utils/receiptCompliance';
import { receiptLinePromo, showReceiptDiscountPrint } from '@/utils/receiptPromo';
import i18n from '@/i18n';
import { getPoweredByI18nKey } from '@/utils/themeDomain';

function t(key: string, opts?: Record<string, unknown>): string {
  return String(i18n.t(key, opts));
}

function fmt(amount: number): string {
  return formatMoney(amount);
}

function appendLinePromoNote(lines: string[], item: ReceiptData['items'][number], show: boolean) {
  if (!show) return;
  const line = receiptLinePromo(item);
  if (!line.hasPromo) return;
  lines.push(
    `[L]  ${t('receipt.was')} ${fmt(line.originalAmount)}  ${t('promotions.lineSaving', { amount: fmt(line.promoAmount) })}`,
  );
}

function appendSellerTaxIds(lines: string[], posSettings?: PosSettings): void {
  for (const row of getSellerTaxLines(posSettings)) {
    lines.push(`[C]${row.label}: ${row.value}`);
  }
}

function appendFbrBlock(lines: string[], inv: ReceiptData, posSettings?: PosSettings, width = 32): void {
  if ((posSettings?.country || 'PK') !== 'PK') return;
  const show =
    inv.fbr_invoice_number
    || inv.fbr_status === 'Submitted'
    || inv.fbr_status === 'Pending'
    || inv.fbr_qr_code
    || posSettings?.enable_fbr_integration;
  if (!show) return;
  lines.push(`[L]${'-'.repeat(width)}`);
  const invNo = inv.fbr_invoice_number || (inv.fbr_status === 'Pending' ? 'FBR sync pending' : '—');
  lines.push(`[C]FBR Inv #: ${invNo}`);
  if (inv.fbr_usin) lines.push(`[C]USIN: ${inv.fbr_usin}`);
  if (inv.fbr_qr_code) lines.push(`[C]QR: ${String(inv.fbr_qr_code).substring(0, Math.min(40, width))}`);
}

function appendZatcaBlock(lines: string[], inv: ReceiptData, posSettings?: PosSettings, width = 32): void {
  if (!shouldShowZatcaBlock(inv, posSettings)) return;
  const summary = getZatcaReceiptSummary(inv, posSettings);
  lines.push(`[L]${'-'.repeat(width)}`);
  if (summary) {
    const title = summary.documentTitleKey.includes('credit') ? t('receipt.creditNote') : t('receipt.simplifiedTaxInvoice');
    lines.push(`[C]${title}`);
    if (summary.sellerVat) lines.push(`[C]VAT/TRN: ${summary.sellerVat}`);
    lines.push(`[C]Date: ${summary.invoiceDateTime}`);
    lines.push(`[C]Total incl VAT: ${summary.totalWithVat.toFixed(2)}`);
    lines.push(`[C]VAT: ${summary.vatAmount.toFixed(2)}`);
  }
  const uuid = inv.zatca_uuid || (inv.zatca_status === 'Pending' ? 'ZATCA sync pending' : '—');
  lines.push(`[C]ZATCA UUID: ${uuid}`);
  if (inv.zatca_icv) lines.push(`[C]ICV: ${inv.zatca_icv}`);
  if (inv.zatca_qr_code) lines.push(`[C]QR: ${String(inv.zatca_qr_code).substring(0, Math.min(40, width))}`);
}

function divider(width: number, char = '-'): string {
  return char.repeat(width);
}

function padRight(text: string, len: number): string {
  if (text.length >= len) return text.substring(0, len);
  return text + ' '.repeat(len - text.length);
}

function padLeft(text: string, len: number): string {
  if (text.length >= len) return text.substring(0, len);
  return ' '.repeat(len - text.length) + text;
}

export function buildTestPrintPayload(companyName = 'Sale Desk'): string {
  const lines = [
    '[C]<b>' + companyName + '</b>',
    '[C]Test Print',
    '[L]' + divider(32),
    '[L]Printer connected successfully',
    '[C]' + new Date().toLocaleString(),
  ];
  return lines.join('\n');
}

export function receiptToDantsu(
  inv: ReceiptData,
  options?: { companyName?: string; paperWidth?: PaperWidth; posSettings?: PosSettings },
): string {
  const companyName = getDisplayCompanyName(inv, options?.posSettings) || options?.companyName || 'Sale Desk';
  const width = options?.paperWidth === '58mm' ? 32 : 42;
  const lines: string[] = [];
  const taxLabel = ((options?.posSettings?.tax_label || 'Tax').trim() || 'Tax');
  const tmplSettings = parseReceiptTemplateSettings(options?.posSettings?.receipt_template_settings);
  const mode = normalizeLayoutMode(tmplSettings.layout_mode);
  // modern prints as compact-like; short is ultra-dense
  const thermalMode = mode === 'modern' ? 'compact' : mode === 'short' ? 'short' : mode;

  // Header
  if (thermalMode === 'short') {
    lines.push(`[C]<b>${companyName}</b>`);
    appendSellerTaxIds(lines, options?.posSettings);
    if (tmplSettings.show_phone && inv.company_phone) lines.push(`[C]${inv.company_phone}`);
  } else if (thermalMode === 'compact') {
    lines.push(`[C]<b>${companyName}</b>`);
    appendSellerTaxIds(lines, options?.posSettings);
    if (tmplSettings.header_text) lines.push(`[C]${tmplSettings.header_text}`);
    const compactContact = [];
    if (tmplSettings.show_address && inv.company_address) compactContact.push(inv.company_address);
    if (tmplSettings.show_phone && inv.company_phone) compactContact.push(inv.company_phone);
    if (compactContact.length > 0) lines.push(`[C]${compactContact.join(' | ')}`);
  } else if (thermalMode === 'elegant') {
    lines.push(`[C]<b><font size="big">${companyName}</font></b>`);
    appendSellerTaxIds(lines, options?.posSettings);
    if (tmplSettings.header_text) lines.push(`[C]${tmplSettings.header_text}`);
    if (tmplSettings.show_address && inv.company_address) lines.push(`[C]${inv.company_address}`);
    const elegantContact = [];
    if (tmplSettings.show_phone && inv.company_phone) elegantContact.push(`Ph: ${inv.company_phone}`);
    if (tmplSettings.show_email && inv.company_email) elegantContact.push(`Email: ${inv.company_email}`);
    if (elegantContact.length > 0) lines.push(`[C]${elegantContact.join(' | ')}`);
  } else {
    lines.push(`[C]<b><font size="big">${companyName}</font></b>`);
    appendSellerTaxIds(lines, options?.posSettings);
    if (tmplSettings.header_text) lines.push(`[C]${tmplSettings.header_text}`);
    if (tmplSettings.show_address && inv.company_address) lines.push(`[C]${inv.company_address}`);
    const contact = [];
    if (tmplSettings.show_phone && inv.company_phone) contact.push(`Ph: ${inv.company_phone}`);
    if (tmplSettings.show_email && inv.company_email) contact.push(`Email: ${inv.company_email}`);
    if (contact.length > 0) lines.push(`[C]${contact.join(' | ')}`);
  }

  lines.push(`[L]${divider(width, thermalMode === 'elegant' ? '=' : '-')}`);

  const d = new Date(inv.creation || inv.invoice_date || new Date());
  const dateStr = d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const timeStr = d.toLocaleTimeString('en-GB', { hour12: false, hour: '2-digit', minute: '2-digit' });
  const dateTimeStr = `${dateStr}, ${timeStr}`;
  const customer = inv.customer_name || t('receipt.walkIn');
  const servedBy = inv.cashier_name || inv.cashier || t('receipt.cashier');

  if (thermalMode === 'short') {
    lines.push(`[L]#${inv.name.substring(Math.max(0, inv.name.length - 10))} ${dateStr}`);
    if (tmplSettings.show_customer) lines.push(`[L]${customer.substring(0, width)}`);
  } else if (thermalMode === 'compact') {
    lines.push(`[L]INV: ${inv.name.substring(Math.max(0, inv.name.length - 12))}`);
    lines.push(`[L]DATE: ${dateStr} ${timeStr}`);
    if (tmplSettings.show_customer) lines.push(`[L]CUST: ${customer.substring(0, 20)}`);
    if (tmplSettings.show_served_by) lines.push(`[L]CASH: ${servedBy.substring(0, 20)}`);
  } else if (thermalMode === 'elegant') {
    lines.push(`[L]<b>${t('receipt.invoiceNo')} :</b> ${inv.name}`);
    lines.push(`[L]<b>${t('receipt.invoiceDateTime')} :</b> ${dateTimeStr}`);
    if (tmplSettings.show_customer) lines.push(`[L]<b>${t('receipt.billedTo')}   :</b> ${customer}`);
    if (tmplSettings.show_served_by) lines.push(`[L]<b>${t('receipt.cashier')}     :</b> ${servedBy}`);
  } else {
    lines.push(`[L]${padRight(t('receipt.invoiceNo'), 14)}: ${inv.name}`);
    lines.push(`[L]${padRight(t('receipt.invoiceDateTime'), 14)}: ${dateTimeStr}`);
    if (tmplSettings.show_customer) lines.push(`[L]${padRight(t('receipt.customer'), 14)}: ${customer}`);
    if (tmplSettings.show_served_by) lines.push(`[L]${padRight(t('receipt.servedBy'), 14)}: ${servedBy}`);
  }

  lines.push(`[L]${divider(width, thermalMode === 'elegant' ? '=' : '-')}`);

  const showPromoPrint = showReceiptDiscountPrint(options?.posSettings);

  if (thermalMode === 'short') {
    // qty x name ........ amt
    for (const item of inv.items) {
      const name = `${item.item_name}${item.variant_name ? ` (${item.variant_name})` : ''}`;
      const left = `${item.quantity}x ${name}`.substring(0, width - 10);
      lines.push(`[L]${padRight(left, width - 10)}${padLeft(fmt(item.amount), 10)}`);
      appendLinePromoNote(lines, item, showPromoPrint);
    }
  } else if (thermalMode === 'compact') {
    lines.push(`[L]<b>${padRight('QTY', 6)}${padLeft('RATE', Math.floor((width - 6) / 2))}${padLeft('AMOUNT', Math.ceil((width - 6) / 2))}</b>`);
    lines.push(`[L]${divider(width, '.')}`);
    for (let i = 0; i < inv.items.length; i++) {
      const item = inv.items[i];
      const desc = `${tmplSettings.show_item_code && item.item_code ? `[${item.item_code}] ` : ''}${item.item_name}${item.variant_name ? ` - ${item.variant_name}` : ''}`;
      lines.push(`[L]${desc}`);
      const mid = Math.floor((width - 6) / 2);
      lines.push(`[L]${padRight(String(item.quantity), 6)}${padLeft(fmt(item.rate), mid)}${padLeft(fmt(item.amount), Math.ceil((width - 6) / 2))}`);
      appendLinePromoNote(lines, item, showPromoPrint);
      if (i < inv.items.length - 1) lines.push(`[L]${divider(width, '.')}`);
    }
  } else if (thermalMode === 'elegant') {
    if (width >= 42) {
      lines.push(`[L]<b>${padRight('DESCRIPTION', 16)}  QTY  ${padLeft('RATE', 10)} ${padLeft('AMOUNT', 10)}</b>`);
    } else {
      lines.push(`[L]<b>${padRight('DESCRIPTION', 12)} QTY ${padLeft('RATE', 8)} ${padLeft('AMOUNT', 8)}</b>`);
    }
    lines.push(`[L]${divider(width, '=')}`);
    for (let i = 0; i < inv.items.length; i++) {
      const item = inv.items[i];
      const desc = `${tmplSettings.show_item_code && item.item_code ? `[${item.item_code}] ` : ''}${item.item_name}${item.variant_name ? ` - ${item.variant_name}` : ''}`;
      lines.push(`[L]${desc}`);
      if (width >= 42) {
        lines.push(`[L]${padRight('', 16)}  ${padRight(String(item.quantity), 3)}  ${padLeft(fmt(item.rate), 10)} ${padLeft(fmt(item.amount), 10)}`);
      } else {
        lines.push(`[L]${padRight('', 12)} ${padRight(String(item.quantity), 2)} ${padLeft(fmt(item.rate), 8)} ${padLeft(fmt(item.amount), 8)}`);
      }
      appendLinePromoNote(lines, item, showPromoPrint);
      if (i < inv.items.length - 1) lines.push(`[L]${divider(width, '-')}`);
    }
  } else {
    if (width >= 42) {
      lines.push(`[L]<b>${padRight('DESCRIPTION', 14)}   ${padLeft('UNIT PRICE', 10)}   QTY   ${padLeft('AMOUNT', 10)}</b>`);
    } else {
      lines.push(`[L]<b>${padRight('DESCRIPTION', 12)}  ${padLeft('PRICE', 7)}  QTY  ${padLeft('AMT', 7)}</b>`);
    }
    lines.push(`[L]${divider(width, '-')}`);
    for (let i = 0; i < inv.items.length; i++) {
      const item = inv.items[i];
      const desc = `${tmplSettings.show_item_code && item.item_code ? `[${item.item_code}] ` : ''}${item.item_name}${item.variant_name ? ` - ${item.variant_name}` : ''}`;
      lines.push(`[L]${desc}`);
      if (width >= 42) {
        lines.push(`[L]${padRight('', 14)}   ${padLeft(fmt(item.rate), 10)}   ${padLeft(String(item.quantity), 5)} ${padLeft(fmt(item.amount), 10)}`);
      } else {
        lines.push(`[L]${padRight('', 12)}  ${padLeft(fmt(item.rate), 7)}  ${padLeft(String(item.quantity), 3)}  ${padLeft(fmt(item.amount), 7)}`);
      }
      appendLinePromoNote(lines, item, showPromoPrint);
      if (i < inv.items.length - 1) lines.push(`[L]${divider(width)}`);
    }
  }

  lines.push(`[L]${divider(width, thermalMode === 'elegant' ? '=' : '-')}`);

  const displayDiscount = (inv.discount_amount || 0) + (inv.write_off_amount || 0);
  const showDisc = displayDiscount > 0 && (!options?.posSettings || options.posSettings.show_discount_in_invoice_print !== false);
  const promoTotal = Number(inv.promo_total) || 0;
  const showPromo = promoTotal > 0 && (!options?.posSettings || options.posSettings.show_discount_in_invoice_print !== false);
  const chargedSub = (inv.item_total || 0) + (inv.service_total || 0);
  const originalSub = chargedSub + promoTotal;
  const showTax = (!options?.posSettings || options.posSettings.show_tax_in_invoice !== false) && (inv.tax_amount || 0) > 0;

  if (thermalMode === 'short') {
    if (showPromo) lines.push(`[L]Promo:${padLeft('− ' + fmt(promoTotal), width - 6)}`);
    if (showDisc) lines.push(`[L]Disc:${padLeft('− ' + fmt(displayDiscount), width - 5)}`);
    if (showTax) lines.push(`[L]${taxLabel}:${padLeft(fmt(inv.tax_amount), width - (taxLabel.length + 1))}`);
    lines.push(`[L]<b>TOTAL:${padLeft(fmt(inv.net_total), width - 6)}</b>`);
    
    if (inv.customer_name && inv.customer_name !== 'Walk In') {
      const prevOut = inv.previous_outstanding || 0;
      const totOut = prevOut + (inv.balance_amount || 0);
      lines.push(`[L]Prev Out:${padLeft(fmt(prevOut), width - 9)}`);
      lines.push(`[L]<b>Total Out:${padLeft(fmt(totOut), width - 10)}</b>`);
    }

    lines.push(`[L]Paid:${padLeft(fmt(inv.paid_amount), width - 5)}`);
  } else if (thermalMode === 'compact') {
    lines.push(`[L]SUBTOTAL:${padLeft(fmt(showPromo ? originalSub : chargedSub), width - 9)}`);
    if (showPromo) lines.push(`[L]PROMO:${padLeft('− ' + fmt(promoTotal), width - 6)}`);
    if (showDisc) lines.push(`[L]DISCOUNT:${padLeft('− ' + fmt(displayDiscount), width - 9)}`);
    if (showTax) lines.push(`[L]${taxLabel.toUpperCase()}:${padLeft(fmt(inv.tax_amount), width - (taxLabel.length + 1))}`);
    lines.push(`[L]${divider(width, '.')}`);
    lines.push(`[L]<b>TOTAL:${padLeft(fmt(inv.net_total), width - 6)}</b>`);
    
    if (inv.customer_name && inv.customer_name !== 'Walk In') {
      const prevOut = inv.previous_outstanding || 0;
      const totOut = prevOut + (inv.balance_amount || 0);
      lines.push(`[L]PREV OUT:${padLeft(fmt(prevOut), width - 9)}`);
      lines.push(`[L]<b>TOT. OUT:${padLeft(fmt(totOut), width - 10)}</b>`);
    }

    lines.push(`[L]PAID:${padLeft(fmt(inv.paid_amount), width - 5)}`);
    if (inv.balance_amount > 0) lines.push(`[L]CHANGE:${padLeft(fmt(inv.balance_amount), width - 7)}`);
  } else if (thermalMode === 'elegant') {
    lines.push(`[L]${padRight('Subtotal', width - 15)}${padLeft(fmt(showPromo ? originalSub : chargedSub), 15)}`);
    if (showPromo) lines.push(`[L]${padRight('Promotions', width - 15)}${padLeft('− ' + fmt(promoTotal), 15)}`);
    if (showDisc) lines.push(`[L]${padRight('Discount', width - 15)}${padLeft('− ' + fmt(displayDiscount), 15)}`);
    if (showTax) lines.push(`[L]${padRight(taxLabel, width - 15)}${padLeft(fmt(inv.tax_amount), 15)}`);
    lines.push(`[L]${divider(width, '-')}`);
    lines.push(`[L]<b>${padRight('TOTAL AMOUNT', width - 15)}${padLeft(fmt(inv.net_total), 15)}</b>`);
    
    if (inv.customer_name && inv.customer_name !== 'Walk In') {
      const prevOut = inv.previous_outstanding || 0;
      const totOut = prevOut + (inv.balance_amount || 0);
      lines.push(`[L]${padRight('Previous Out.', width - 15)}${padLeft(fmt(prevOut), 15)}`);
      lines.push(`[L]<b>${padRight('Total Out.', width - 15)}${padLeft(fmt(totOut), 15)}</b>`);
    }

    lines.push(`[L]${padRight('Amount Paid', width - 15)}${padLeft(fmt(inv.paid_amount), 15)}`);
    if (inv.balance_amount > 0) lines.push(`[L]${padRight('Balance Due', width - 15)}${padLeft(fmt(inv.balance_amount), 15)}`);
  } else {
    lines.push(`[L]${padRight('SUBTOTAL', width - 12)}${padLeft(fmt(showPromo ? originalSub : chargedSub), 12)}`);
    if (showPromo) lines.push(`[L]${padRight('Promotions', width - 12)}${padLeft('− ' + fmt(promoTotal), 12)}`);
    if (showDisc) lines.push(`[L]${padRight('Discount', width - 12)}${padLeft('− ' + fmt(displayDiscount), 12)}`);
    if (showTax) lines.push(`[L]${padRight(taxLabel, width - 12)}${padLeft(fmt(inv.tax_amount), 12)}`);
    lines.push(`[L]${divider(width)}`);
    lines.push(`[L]<b>${padRight('TOTAL', width - 12)}${padLeft(fmt(inv.net_total), 12)}</b>`);
    
    if (inv.customer_name && inv.customer_name !== 'Walk In') {
      const prevOut = inv.previous_outstanding || 0;
      const totOut = prevOut + (inv.balance_amount || 0);
      lines.push(`[L]${padRight('PREV OUT', width - 12)}${padLeft(fmt(prevOut), 12)}`);
      lines.push(`[L]<b>${padRight('TOTAL OUT', width - 12)}${padLeft(fmt(totOut), 12)}</b>`);
    }

    if (inv.payments?.length) {
      for (const p of inv.payments) {
        lines.push(`[L]${padRight('Payment Method', width - 12)}${padLeft(p.payment_method, 12)}`);
      }
    } else {
      lines.push(`[L]${padRight('Payment Method', width - 12)}${padLeft('Cash', 12)}`);
    }
    lines.push(`[L]${divider(width)}`);
    lines.push(`[L]<b>${padRight('PAID', width - 12)}${padLeft(fmt(inv.paid_amount), 12)}</b>`);
    if (inv.balance_amount > 0) {
      lines.push(`[L]${divider(width)}`);
      lines.push(`[L]${padRight('CHANGE', width - 12)}${padLeft(fmt(inv.balance_amount), 12)}`);
    }
  }

  if (options?.posSettings?.prices_include_tax) {
    lines.push(`[C]${t('receipt.pricesIncludeTax')}`);
  }

  appendFbrBlock(lines, inv, options?.posSettings, width);
  appendZatcaBlock(lines, inv, options?.posSettings, width);

  lines.push(`[L]${divider(width, thermalMode === 'elegant' ? '=' : '-')}`);

  if (tmplSettings.footer_text) {
    lines.push(`[C]${tmplSettings.footer_text}`);
  } else if (thermalMode === 'short') {
    lines.push(`[C]${t('receipt.thankYou')}`);
  } else if (thermalMode === 'elegant') {
    lines.push(`[C]${t('receipt.thankYouPatronage')}`);
  } else {
    lines.push(`[C]${t('receipt.thankYou')}`);
  }
  if (thermalMode !== 'short') {
    lines.push(`[C]${t(getPoweredByI18nKey())}`);
  }

  return lines.join('\n');
}
